import { i18n } from "@tellescope/i18n";

/**
 * Utility functions for MongoDB aggregation pipeline validation and formatting.
 */

/**
 * MongoDB aggregation stages that are forbidden for security/safety reasons.
 *
 * These stages could:
 * - Modify data ($out, $merge)
 * - Execute arbitrary code ($function, $accumulator)
 * - Access system collections or perform lookups that bypass access control
 */
const FORBIDDEN_STAGES = [
  "$out", // Writes aggregation results to a collection
  "$merge", // Merges aggregation results into a collection
  "$function", // Executes JavaScript function
  "$accumulator", // Custom accumulator with JavaScript
  "$planCacheStats", // Returns plan cache statistics
  "$currentOp", // Returns information on active/dormant operations
  "$listSessions", // Lists active sessions
  "$listLocalSessions", // Lists local sessions
];

/**
 * Validates a MongoDB aggregation pipeline for forbidden stages.
 *
 * @param pipeline - MongoDB aggregation pipeline array
 * @returns Array of validation error messages (empty if valid)
 *
 * @example
 * ```typescript
 * const pipeline = [
 *   { $match: { status: "active" } },
 *   { $group: { _id: "$status", count: { $sum: 1 } } }
 * ];
 * const errors = validateAggregationPipeline(pipeline);
 * // errors = [] (valid)
 *
 * const badPipeline = [
 *   { $match: { status: "active" } },
 *   { $out: "output_collection" }
 * ];
 * const errors = validateAggregationPipeline(badPipeline);
 * // errors = ["Forbidden aggregation stage: $out"]
 * ```
 */
export function validateAggregationPipeline(pipeline: Array<object>): string[] {
  const errors: string[] = [];

  for (const stage of pipeline) {
    const stageKeys = Object.keys(stage);

    for (const key of stageKeys) {
      if (FORBIDDEN_STAGES.includes(key)) {
        errors.push(i18n.t("Forbidden aggregation stage: {{stage}}", { ns: "ai-agent", stage: key }));
      }
    }
  }

  return errors;
}

/**
 * Formats aggregation results for display to the LLM.
 *
 * Converts aggregation output into a readable string format that the LLM
 * can parse and use to answer user questions.
 *
 * @param results - Array of aggregation result documents
 * @param modelName - Name of the model being queried (for context)
 * @returns Formatted string describing the results
 *
 * @example
 * ```typescript
 * const results = [
 *   { _id: "active", count: 42 },
 *   { _id: "inactive", count: 8 }
 * ];
 * const formatted = formatAggregationResults(results, "endusers");
 * // Returns: "Aggregation results for endusers (2 results):\n[{...}]"
 * ```
 */
export function formatAggregationResults(results: Array<object>, modelName: string): string {
  if (results.length === 0) {
    return i18n.t("No results found for {{modelName}} aggregation", { ns: "ai-agent", modelName });
  }

  const summary = i18n.t("Aggregation results for {{modelName}} ({{count}} result)", {
    ns: "ai-agent",
    modelName,
    count: results.length,
  });
  const jsonResults = JSON.stringify(results, null, 2);

  return `${summary}\n\n${jsonResults}`;
}

/**
 * Generates field documentation from a Zod schema for LLM context.
 *
 * Extracts field names and descriptions from a Zod object schema to help
 * the LLM understand what fields are available for aggregation.
 *
 * @param schema - Zod object schema with field descriptions
 * @returns Formatted string describing available fields
 *
 * @example
 * ```typescript
 * import { z } from "zod";
 * const schema = z.object({
 *   name: z.string().describe("User's full name"),
 *   age: z.number().describe("User's age in years"),
 * });
 * const docs = generateFieldDocumentation(schema);
 * // Returns: "Available fields:\n- name: User's full name\n- age: User's age in years"
 * ```
 */
export function generateFieldDocumentation(schema: any): string {
  const shape = schema._def.shape();
  const fields = Object.keys(shape);

  const fieldDocs = fields
    .map((fieldName) => {
      const fieldSchema = shape[fieldName];
      const description = fieldSchema._def.description || "No description";
      return `- ${fieldName}: ${description}`;
    })
    .join("\n");

  return `Available fields:\n${fieldDocs}`;
}
